import type { UpdateUserDto, UserDto } from './user.schema';
import { queryOptions, replaceEqualDeep, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getMarketSourcePriority } from '@/libs/market/market';
import { useAppSelector } from '@/redux/hooks';
import { QueriesKeys } from '../queriesKeys';
import { userApi } from './user.api';

export function mergeUserQueryData(
  previous: UserDto | undefined,
  incoming: UserDto,
): UserDto {
  if (!previous || previous.id !== incoming.id || !previous.market || !previous.marketSource) {
    return replaceEqualDeep(previous, incoming);
  }

  const previousPriority = getMarketSourcePriority(previous.marketSource);
  const incomingPriority = getMarketSourcePriority(incoming.marketSource);
  const previousUpdatedAt = Date.parse(previous.updatedAt);
  const incomingUpdatedAt = Date.parse(incoming.updatedAt);
  const previousIsNewer = !Number.isNaN(previousUpdatedAt)
    && (Number.isNaN(incomingUpdatedAt) || previousUpdatedAt > incomingUpdatedAt);
  const previousMarketWins = previousPriority > incomingPriority
    || (previousPriority === incomingPriority && previousIsNewer);
  const baseUser = previousIsNewer ? previous : incoming;
  const marketUser = previousMarketWins ? previous : incoming;

  return replaceEqualDeep(previous, {
    ...baseUser,
    market: marketUser.market,
    marketSource: marketUser.marketSource,
  });
}

export const userMeQueryOptions
  = queryOptions({
    queryKey: [QueriesKeys.user],
    queryFn: () => userApi.getUserMe(),
    structuralSharing: (previous, incoming) => mergeUserQueryData(
      previous as UserDto | undefined,
      incoming as UserDto,
    ),
  });

export const useUserMeQuery = () => {
  const { userSession } = useAppSelector(state => state.user);

  return useQuery({
    ...userMeQueryOptions,
    enabled: userSession,
  });
};

/** Shared so every invalidation site stays in step with the query itself. */
export const paymentMethodsQueryKey = [QueriesKeys.user, QueriesKeys.paymentMethods];

export const usePaymentMethodsQuery = () => {
  const { userSession } = useAppSelector(state => state.user);
  return useQuery({
    queryKey: paymentMethodsQueryKey,
    queryFn: () => userApi.getUserPaymentMethods(),
    enabled: userSession,
  });
};

export const useUpdateUserMeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateUserDto) => userApi.patchUserMe(data),
    onSuccess: (user) => {
      queryClient.setQueryData([QueriesKeys.user], user);
    },
  });
};

export const useVerifyPaymentMethodMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, cardId }: { userId: string; cardId: string }) => {
      return userApi.verifyPaymentMethod(userId, cardId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentMethodsQueryKey });
    },
  });
};
